import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { AdminOrderSearchCriterion } from '@lds-efsp/shared-contracts';
import { ADMIN_ORDER_SEARCH_CRITERION_IDS } from '@lds-efsp/shared-contracts';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminDataTable from '../../../shared/components/AdminDataTable';
import AdminSubNav from '../../../shared/components/AdminSubNav';
import AdminFilterBar from '../../../shared/components/AdminFilterBar';
import AdminHeader from '../../../shared/components/AdminHeader';
import AdminServiceSelectionModal from '../../../shared/components/AdminServiceSelectionModal';
import { StatusBadge, OrderTypeBadge, SourceBadge } from '../components/StatusBadge';
import { ORDER_STATUS_LABELS } from '../components/status-config';
import { PriorityBadge } from '../../work/components/PriorityBadge';
import { OrderStatusPreviewPopover } from '../components/OrderStatusPreview';
import { useAdminCompany } from '../../../shared/contexts/AdminCompanyContext';
import { useAdminOrdersApi } from '../../../api/useAdminOrdersApi';
import { decideAdminOrdersView, matchesViewTab, type AdminOrdersActiveSearch } from '../lib/admin-orders-view';
import { formatReceivedDate } from '../lib/format-received-date';

// Concierge styling
const CONCIERGE_BORDER = '#7C3AED';

// Crown icon for concierge accounts
const CrownIcon: React.FC = () => (
  <svg className="w-4 h-4 mr-1 inline-block shrink-0" viewBox="0 0 24 24" fill="#7C3AED" data-testid="concierge-icon">
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 2h14v2H5v-2z"/>
  </svg>
);

// Filter options recomputed from the real status labels — the demo's option
// list was keyed on the invented displayStatus field, now gone.
const STATUS_FILTER_OPTIONS = Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => ({ value, label }));

interface AdminOrdersListProps {
  activeSubSection: string;
  onSubSectionChange: (subSection: string) => void;
  brand: BrandConfig;
  Icons: IconsType;
}

const AdminOrdersList: React.FC<AdminOrdersListProps> = ({ activeSubSection, onSubSectionChange, brand, Icons }) => {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { selectedCompanyId, selectedCompany } = useAdminCompany();

  // The URL is the one writer of the active search: the submit path in
  // AdminLayout writes `search` + `search_field`, and this page reads them
  // back into the view decider's seam. Anything else — an absent param or a
  // criterion outside the registry vocabulary in a hand-edited URL — reads as
  // no search, so the decider answers the unfiltered rows and a corrupted
  // criterion can never reach the route's raw zod-enum 400.
  const activeSearch: AdminOrdersActiveSearch | null = useMemo(() => {
    const term = searchParams.get('search');
    const criterion = searchParams.get('search_field');
    if (term === null || criterion === null) return null;
    if (!(ADMIN_ORDER_SEARCH_CRITERION_IDS as readonly string[]).includes(criterion)) return null;
    return { criterion: criterion as AdminOrderSearchCriterion, term };
  }, [searchParams]);

  const { orders, isLoading, error, refetch } = useAdminOrdersApi(activeSearch);

  // Refetch whenever the impersonated company changes — useApi sends
  // X-Account-Id, and the client-side account filter below keys off it too.
  // A changed search re-creates `refetch`, so the same effect re-runs for it.
  useEffect(() => {
    refetch();
  }, [refetch, selectedCompanyId]);

  const companyRows = useMemo(() => orders.filter((o) => !selectedCompanyId || o.accountId === selectedCompanyId), [orders, selectedCompanyId]);

  // Exactly one of three views renders, decided by the pure decider: the
  // unfiltered rows when no search is active (R4), the matching rows when one
  // is and something matched (R6), the empty search result when the active
  // search matched nothing (R5).
  const view = useMemo(() => decideAdminOrdersView(companyRows, activeSearch), [companyRows, activeSearch]);
  // A search's result spans the whole list: the sub-nav tab is a browsing
  // scope, not a search scope, and clipping an active search's rows to the
  // tab the submission was made from would hide a match living in another tab
  // behind an empty table. Clearing the search restores the tab scope.
  const visibleRows = useMemo(() => (view.kind === 'empty' ? [] : activeSearch ? [...view.rows] : view.rows.filter((o) => matchesViewTab(o, activeSubSection))), [view, activeSearch, activeSubSection]);

  // Dynamic sub-nav counts over the company-scoped rows
  const subNavCounts = useMemo(() => ({
    incoming: companyRows.filter((o) => matchesViewTab(o, 'incoming')).length,
    pending: companyRows.filter((o) => matchesViewTab(o, 'pending')).length,
    executed: companyRows.filter((o) => matchesViewTab(o, 'executed')).length,
    closed: companyRows.filter((o) => matchesViewTab(o, 'closed')).length,
    cases: companyRows.filter((o) => matchesViewTab(o, 'cases')).length,
  }), [companyRows]);

  // Dynamic sub-nav items with counts based on filtered data
  const subNavItems = useMemo(() => [
    { key: 'incoming', label: 'Incoming', count: subNavCounts.incoming || undefined },
    { key: 'pending', label: 'Pending', count: subNavCounts.pending || undefined },
    { key: 'executed', label: 'Executed', count: subNavCounts.executed || undefined },
    { key: 'closed', label: 'Closed', count: subNavCounts.closed || undefined },
    { key: 'cases', label: 'Cases', count: subNavCounts.cases || undefined },
  ], [subNavCounts]);

  const handleServiceSelect = (serviceId) => {
    console.log('Service selected:', serviceId, 'for company:', selectedCompany);
    // TODO: Navigate to order creation form with service type and company context
  };

  // Custom row styling for concierge accounts
  const getRowStyle = (row) => ({
    borderLeft: row.accountType === 'concierge' ? `4px solid ${CONCIERGE_BORDER}` : '4px solid transparent'
  });

  const columns = [
    {
      key: 'receivedDate',
      label: 'Received',
      sortable: true,
      filterable: true,
      render: (value: string) => (
        <div className="text-sm" style={{ color: brand.colors.text }}>
          {formatReceivedDate(value, { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      )
    },
    {
      key: 'orderType',
      label: 'Order Type',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex flex-col gap-1">
          <OrderTypeBadge type={value} brand={brand} />
          <PriorityBadge priority={row.priority} brand={brand} />
        </div>
      )
    },
    {
      key: 'account',
      label: 'Account',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <span className="flex items-center">
          {row.accountType === 'concierge' && <CrownIcon />}
          {value}
        </span>
      )
    },
    {
      key: 'caseNumber',
      label: 'Case',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex flex-col">
          <div className="text-sm font-medium" style={{ color: brand.colors.text }}>{value ?? '—'}</div>
          <div className="text-xs truncate max-w-[200px]" style={{ color: brand.colors.textLight }}>{row.caseTitle}</div>
        </div>
      )
    },
    {
      key: 'orderNumber',
      label: 'Order #',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <span className="flex items-center" style={{ color: brand.colors.link }}>
          {row.accountType === 'concierge' && <CrownIcon />}
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: STATUS_FILTER_OPTIONS,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={value} brand={brand} />
          <OrderStatusPreviewPopover orderId={row.id} brand={brand} />
        </div>
      )
    },
    {
      key: 'source',
      label: 'Source',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'portal', label: 'Portal' },
        { value: 'concierge', label: 'Concierge' },
        { value: 'api', label: 'API' },
      ],
      render: (value: string) => <SourceBadge source={value as 'portal' | 'concierge' | 'api'} />
    },
  ];

  const getSectionTitle = () => {
    switch (activeSubSection) {
      case 'incoming': return 'Incoming Orders';
      case 'pending': return 'Pending Orders';
      case 'executed': return 'Executed Orders';
      case 'closed': return 'Closed Orders';
      case 'cases': return 'Cases';
      default: return 'Orders';
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader title={getSectionTitle()} brand={brand} Icons={Icons} showCompanySelector={false}>
        <button
          onClick={() => setIsServiceModalOpen(true)}
          className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2"
          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          data-testid="place-order-button"
        >
          <Icons.Plus className="w-4 h-4" />
          Place Order
        </button>
        <button
          className="px-4 py-2 text-sm font-medium rounded-lg border"
          style={{ borderColor: brand.colors.border, color: brand.colors.text }}
        >
          Bulk Update
        </button>
      </AdminHeader>

      <AdminSubNav
        items={subNavItems}
        activeItem={activeSubSection}
        onItemChange={onSubSectionChange}
        brand={brand}
      />

      <AdminFilterBar module="orders" brand={brand} Icons={Icons} />

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div
            className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm"
            role="alert"
            data-testid="admin-orders-error"
          >
            {error}
          </div>
        )}
        {view.kind === 'empty' && !isLoading ? (
          // The empty search result is a state of the SEARCH (an active search
          // matched nothing) — it replaces the table entirely, and only after
          // loading: rows arrive as [] while the request is in flight, and a
          // skeleton must never flash as "no results". It carries its own test
          // id and the submitted term; the unfiltered rows render only when no
          // search is active, never here.
          <div
            className="flex-1 flex flex-col items-center justify-center gap-1 py-16 text-center"
            data-testid="admin-orders-search-empty"
          >
            <p className="text-sm font-medium" style={{ color: brand.colors.text }}>
              No orders match your search
            </p>
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              No results for "{activeSearch?.term}". Try a different term.
            </p>
          </div>
        ) : (
          <AdminDataTable
            columns={columns}
            data={visibleRows}
            totalCount={visibleRows.length}
            showCheckboxes={false}
            showPagination={true}
            isLoading={isLoading}
            onRowClick={(row) => console.log('Row clicked:', row)}
            brand={brand}
            Icons={Icons}
            getRowStyle={getRowStyle}
          />
        )}
      </div>

      <AdminServiceSelectionModal
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
        onSelectService={handleServiceSelect}
        selectedCompany={selectedCompany}
        brand={brand}
        Icons={Icons}
      />
    </div>
  );
};

export default AdminOrdersList;
